import { BookOpen, Box, Boxes, Footprints, LayoutGrid, Layers, Watch, type LucideIcon } from "lucide-react";

/**
 * BROWSE category taxonomy — shared by the desktop `BrowseSidebar`, the landing
 * `ShopByCategory` tiles, and the `/category/<slug>` page. Kept in a plain
 * (non-"use client") module so **server** components (the category route's
 * `generateMetadata`) can read the labels too — importing it from the
 * "use client" `browse-sidebar` turned the array into a client reference and
 * `.find` blew up at request time.
 *
 * `id` doubles as the route slug (`/category/<id>`); "all" clears the filter.
 */
export type BrowseCategory = {
  id: string;
  label: string;
  /** Compact display for tight surfaces (landing tiles) — falls back to `label`. */
  shortLabel?: string;
  icon: LucideIcon;
};

// Order = LIVE categories first (lego, trading-cards, comics — real
// inventory behind them), then the coming-soon teasers. Promote a category
// by moving it above the coming-soon block when its slice ships.
export const BROWSE_CATEGORIES: BrowseCategory[] = [
  { id: "all", label: "All items", icon: LayoutGrid },
  { id: "lego", label: "LEGO", icon: Box },
  // "Trading Cards" wraps/crowds the narrow mobile tile → tiles show "TCG";
  // the sidebar and category-page title keep the full label.
  { id: "trading-cards", label: "Trading Cards", shortLabel: "TCG", icon: Layers },
  { id: "comics", label: "Comics", icon: BookOpen },
  { id: "sneakers", label: "Sneakers", icon: Footprints },
  { id: "figures", label: "Figures", icon: Boxes },
  { id: "watches", label: "Watches", icon: Watch },
];

/**
 * Categories with a real product cutout at `public/categories/<id>.webp` — the
 * filename IS the category id, so every render site builds the src as a
 * root-relative template literal (`/categories/${id}.webp`). That shape is what
 * lets `fitness-next-image-params` prove an `unoptimized` src is a local asset;
 * an id→path Record is opaque to its one-hop scan and reads as a raw-storage-URL
 * violation. Keep the convention when adding a photo. Shared by the landing
 * tiles, the BROWSE sidebar rows, and the category coming-soon medallion.
 * A category not in this set falls back to its Lucide `icon` above.
 */
export const CATEGORY_PHOTO_IDS: ReadonlySet<string> = new Set([
  "lego",
  "sneakers",
  "trading-cards",
  "watches",
  "comics",
  "figures",
]);
